import { appState } from './appState';
import { dom } from './dom';
import { startDemoLoop, stopDemoLoop } from './demo';
import { setSeoState, t } from './i18n';
import { store } from './store';
import { AppState, SubscriptionLevel } from './types';
export function toggleTheme() {
    const html = document.documentElement;
    const sun = document.getElementById('sunIcon');
    const moon = document.getElementById('moonIcon');
    if (html.getAttribute('data-bs-theme') === 'dark') {
        html.setAttribute('data-bs-theme', 'light');
        sun?.classList.remove('d-none');
        moon?.classList.add('d-none');
    }
    else {
        html.setAttribute('data-bs-theme', 'dark');
        sun?.classList.add('d-none');
        moon?.classList.remove('d-none');
    }
}
export function setAppState(state) {
    appState.currentAppState = state;
    updateUI();
}
export function updateUI() {
    const isReady = appState.isModelLoaded;
    const user = store.getUser();
    const isLoggedIn = Boolean(user);
    const hasByokKeys = store.getByokKeys().length > 0;
    // Allow action if logged in OR has BYOK keys configured
    const canUseApp = isLoggedIn || hasByokKeys;
    dom.mainActionBtn.disabled = !isReady || !canUseApp || (dom.inputText.value.trim() === '' && appState.uploadedFiles.length === 0);
    dom.inputText.disabled = !canUseApp;
    dom.inputText.placeholder = canUseApp
        ? appState.defaultInputPlaceholder
        : t('compose.noApiKeyPlaceholder');
    if (dom.memoryStatusBadge) {
        const memoryEnabled = appState.memoryEnabled;
        dom.memoryStatusBadge.textContent = memoryEnabled
            ? t('status.memoryOn')
            : t('status.memoryOff');
        dom.memoryStatusBadge.classList.toggle('bg-success-subtle', memoryEnabled);
        dom.memoryStatusBadge.classList.toggle('text-success', memoryEnabled);
        dom.memoryStatusBadge.classList.toggle('bg-secondary-subtle', !memoryEnabled);
        dom.memoryStatusBadge.classList.toggle('text-secondary', !memoryEnabled);
        // Show memory badge for both logged in and anonymous users with BYOK keys
        dom.memoryStatusBadge.classList.toggle('d-none', !canUseApp);
    }
    // Show demo only when no user AND no BYOK keys
    if (isLoggedIn || hasByokKeys) {
        stopDemoLoop();
    }
    else {
        startDemoLoop();
    }
    const seoContent = document.getElementById('seoContent');
    // Hide SEO content when user is logged in OR when a chat is active
    const hasActiveChat = Boolean(appState.currentProjectId && appState.currentChatId);
    if (seoContent) {
        seoContent.hidden = isLoggedIn || hasActiveChat;
    }
    setSeoState(isLoggedIn || hasActiveChat);
    const loginSectionTop = document.getElementById('loginSectionTop');
    const loggedOutUI = document.getElementById('loggedOutUI');
    const loggedInUI = document.getElementById('loggedInUI');
    const loggedOutUIMobile = document.getElementById('loggedOutUIMobile');
    const loggedInUIMobile = document.getElementById('loggedInUIMobile');
    const userNameEl = document.getElementById('userName');
    const userNameMobile = document.getElementById('userNameMobile');
    const menuUserName = document.getElementById('menuUserName');
    const menuUserNameMobile = document.getElementById('menuUserNameMobile');
    const menuUserEmail = document.getElementById('menuUserEmail');
    const menuUserEmailMobile = document.getElementById('menuUserEmailMobile');
    const upgradeMenuItem = document.getElementById('upgradeMenuItem');
    const upgradeMenuItemMobile = document.getElementById('upgradeMenuItemMobile');
    const userAvatar = document.getElementById('userAvatar');
    const userAvatarMobile = document.getElementById('userAvatarMobile');
    const freeBadge = document.getElementById('freeBadge');
    const freeBadgeMobile = document.getElementById('freeBadgeMobile');
    if (user) {
        loginSectionTop?.classList.add('d-none');
        loggedOutUI?.classList.add('d-none');
        loggedInUI?.classList.remove('d-none');
        loggedOutUIMobile?.classList.add('d-none');
        loggedInUIMobile?.classList.remove('d-none');
        if (userNameEl)
            userNameEl.textContent = user.name;
        if (menuUserName)
            menuUserName.textContent = user.name;
        if (menuUserEmail)
            menuUserEmail.textContent = user.email;
        if (userAvatar) {
            userAvatar.textContent = user.name.split(' ').map(name => name[0]).join('').toUpperCase();
        }
        if (userNameMobile)
            userNameMobile.textContent = user.name;
        if (menuUserNameMobile)
            menuUserNameMobile.textContent = user.name;
        if (menuUserEmailMobile)
            menuUserEmailMobile.textContent = user.email;
        if (userAvatarMobile) {
            userAvatarMobile.textContent = user.name.split(' ').map(name => name[0]).join('').toUpperCase();
        }
        if (user.level === SubscriptionLevel.ENTERPRISE) {
            upgradeMenuItem?.classList.add('d-none');
            freeBadge?.classList.add('d-none');
            upgradeMenuItemMobile?.classList.add('d-none');
            freeBadgeMobile?.classList.add('d-none');
        }
        else {
            upgradeMenuItem?.classList.remove('d-none');
            freeBadge?.classList.remove('d-none');
            upgradeMenuItemMobile?.classList.remove('d-none');
            freeBadgeMobile?.classList.remove('d-none');
        }
    }
    else {
        // Anonymous user (not logged in) - show anonymous menu with BYOK and Settings
        // Hide login buttons (loginSectionTop, loggedOutUI)
        loginSectionTop?.classList.add('d-none');
        loggedOutUI?.classList.add('d-none');
        loggedOutUIMobile?.classList.add('d-none');
        // Show anonymous user menu (always visible for anonymous users)
        const anonymousUI = document.getElementById('anonymousUI');
        const anonymousUIMobile = document.getElementById('anonymousUIMobile');
        anonymousUI?.classList.remove('d-none');
        anonymousUIMobile?.classList.remove('d-none');
        // Hide logged-in UI
        loggedInUI?.classList.add('d-none');
        loggedInUIMobile?.classList.add('d-none');
    }
    const addProjectBtn = document.getElementById('addProjectBtn');
    const addProjectBtnMobile = document.getElementById('addProjectBtnMobile');
    const addChatBtn = document.getElementById('addChatBtn');
    const addChatBtnMobile = document.getElementById('addChatBtnMobile');
    // Enable project and chat buttons for both logged in and anonymous users
    [addProjectBtn, addProjectBtnMobile, addChatBtn, addChatBtnMobile].forEach(btn => {
        if (btn) {
            btn.disabled = !canUseApp;
        }
    });
    if (appState.currentAppState === AppState.WRITE) {
        dom.workflowHeader.classList.add('d-none');
        document.getElementById('mainTabsHeader')?.classList.add('d-none');
        document.getElementById('interactionCard')?.classList.remove('expanded-mode');
        document.getElementById('composeToolbar')?.classList.remove('d-none');
        dom.btnText.textContent = t('compose.anonymize');
        dom.sendIcon.setAttribute('class', 'bi bi-arrow-up-short fs-4');
        dom.sendIcon.classList.remove('d-none');
        dom.mainActionBtn.classList.remove('btn-success');
        dom.mainActionBtn.classList.add('btn-primary', 'btn-gradient-primary');
        dom.composeTab.click();
        dom.diffTab.classList.add('disabled');
        dom.anonymizedTab.classList.add('disabled');
        dom.entitiesTab.classList.add('disabled');
        if (!dom.inputText.disabled) {
            dom.inputText.focus();
        }
    }
    else if (appState.currentAppState === AppState.REVIEW) {
        dom.workflowHeader.classList.remove('d-none');
        document.getElementById('mainTabsHeader')?.classList.remove('d-none');
        document.getElementById('interactionCard')?.classList.add('expanded-mode');
        document.getElementById('composeToolbar')?.classList.add('d-none');
        dom.workflowState.textContent = t('compose.workflowStateReview');
        dom.diffTab.classList.remove('disabled');
        dom.anonymizedTab.classList.remove('disabled');
        dom.entitiesTab.classList.remove('disabled');
        dom.diffTab.click();
    }
}
